import { TableProps } from "antd-v5";
import { getVocabulary } from "components/Vocabulary";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import { useFindingBucket } from "contexts/FindingBucketContext";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import useAPIResponse from "hooks/useAPIResponse";
import useContainerWidth from "hooks/useContainerWidth";
import { EntityNavigatorParams } from "hooks/useEntityNavigator";
import { getFindingIdColumn } from "hooks/useFindingColumns";
import usePaginationControls from "hooks/usePaginationControls";
import _, {
  compact,
  find,
  forEach,
  isEmpty,
  isNumber,
  isString,
  isUndefined,
  map,
  pick,
  uniq,
} from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FindingAPI from "restAPI/FindingAPI";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { FindingFiltersRequestDto } from "types/api/findingFiltersRequestDto";
import { FindingSummary } from "types/api/findingSummary";
import { ListFindingResponse } from "types/api/listFindingResponse";
import { NameIdPair } from "types/api/nameIdPair";
import { SortOrder as FindingListSortOrder } from "types/api/sortOrder";
import {
  FilterInfo,
  FilterOptionInfo,
  FilterOptionNameId,
  FilterValue,
} from "types/pagination";
import { ARMORCODE_GROUP_KEY } from "utils/armorcodeGroupUtil";
import { CellRenderers } from "utils/tableUtils";

import ACPaginationBar from "ac-components/ACPaginationBar/ACPaginationBar";
import ACSeverityTag, { SeverityType } from "ac-components/ACSeverityTag";
import ACTable from "ac-components/ACTable/ACTable";
import ACColumnDragWrapper from "ac-components/ac-column-drag-wrapper/ACColumnDragWrapper";
import ACFindingBreakdownWrapper from "ac-components/ac-finding-breakdown-wrapper/ACFindingBreakdownWrapper";
import LoadingIcon from "ac-components/ac-icons/LoadingIcon";
import ACMultiGroupingTable, {
  ACMultiGroupingTableContext,
  CountCellRenderer,
  FetchStats,
  GroupStats,
  RenderFinalItems,
  RenderMethodType,
} from "ac-components/ac-multi-grouping-table/ACMultiGroupingTable";
import RiskRatingTag, {
  RiskRating,
} from "ac-components/ac-risk-rating-tag/ACRiskRatingTag";

import {
  ALL_SEVERITIES,
  FINDING_STATUS_VOCABULARY_MAP,
  KEYS_WITH_IDS,
  PartialSearchSupportedFields,
  RENDER_FINDING_SLA_STATUS_FILTERS,
  checkIfDateColumns,
  getCustomColumnTitles,
} from "../Constants";
import { renderBooleanValues } from "../FindingsListV3";
import {
  convertAggField,
  disableSelectAllFindings,
  extractTagValue,
  getFilteredTagKeys,
  getIncludeKeysField,
} from "../utils";
import {
  FindingBreakdownProvider,
  useFindingBreakdownProvider,
} from "./FindingBreakdownProvider";
import { useFindingListContext } from "./FindingListProvider";
import GroupByDescription from "./GroupByDescription";
import { CommonTableProps } from "./common";

type Props = {
  groupBy: string[];
};

type GroupStatsResponse = Record<string, Record<string, number>>;

export default function FindingListGroupByTable({ groupBy }: Props) {
  const [otherGroupsVisible, setOtherGroupsVisible] =
    useState<Record<string, boolean>>();
  const { businessUnitId } = useActiveUserContext();
  const {
    actualAppliedFilters,
    filterValues,
    allColumns,
    setTotalGroupCount,
    productList,
    subProductList,
    showAllTags,
  } = useFindingListContext();

  const [excludeAggregationCardinality] = useAPIResponse(
    () => GlobalSettingAPI.getExcludeAggregationCardinality(),
    [],
  );

  useEffect(() => {
    setTotalGroupCount(undefined);
    if (!groupBy) {
      return;
    }
    const visibility: Record<string, boolean> = {};
    if (groupBy[1]) {
      visibility.group2Count = true;
    }
    if (groupBy[2]) {
      visibility.group3Count = true;
    }
    setOtherGroupsVisible(visibility);
    return () => setTotalGroupCount(undefined);
  }, [groupBy, setTotalGroupCount]);

  const [ref, width] = useContainerWidth<HTMLDivElement>({
    // change prop livemeasure to retrigger width calculation as it is changing from list view to grouped view
    liveMeasure: !isEmpty(groupBy),
  });

  const fetchStats = useCallback<FetchStats>(
    ({ filters, groupByField, otherGroupByFields }) => {
      const filteredTagKeys = getFilteredTagKeys(filters);
      const { promise, cancel } = FindingAPI.getGroupStats({
        aggField: convertAggField(groupByField),
        secAggFieldForCount: uniq(otherGroupByFields.map(convertAggField)),
        filters: {
          ...filters,
        },
        timezone: moment.tz.guess(),
        keysToBeIncludedInAgg: getIncludeKeysField([groupByField]).concat(
          ...(groupByField === "tags" && !showAllTags ? filteredTagKeys : []),
        ),
        keysToBeIncludedInSecAgg: getIncludeKeysField(
          otherGroupByFields,
        ).concat(
          otherGroupByFields.includes("tags") && !showAllTags
            ? filteredTagKeys
            : [],
        ),
        includeMissing: true,
      });
      const updatedPromise = promise.then((response: GroupStatsResponse) => {
        const stats: GroupStats[] = [];
        for (const i in response) {
          const stat: GroupStats = {
            name: i,
            displayName: i,
            riskScore: response[i].riskScore,
            count: response[i].count,
            stats: pick(response[i], groupBy),
          };
          // Some fields are tranlated in the API like team <> teamId, FE has team, BE returns teamId, so converting them in response
          groupBy.forEach((key) => {
            if (
              isUndefined(stat.stats[key]) &&
              !isUndefined(response[i][convertAggField(key)])
            ) {
              stat.stats[key] = response[i][convertAggField(key)];
            }
          });
          stats.push(stat);
        }
        return stats;
      });
      return {
        cancel,
        promise: updatedPromise,
      };
    },
    [groupBy, showAllTags],
  );
  const fetchAllStats = useCallback<FetchStats>(
    ({ filters }) => {
      const filteredTagKeys = getFilteredTagKeys(filters);
      const { promise, cancel } = FindingAPI.getGroupStats({
        // riskScore: true,
        aggField: "businessUnit",
        secAggFieldForCount: uniq(groupBy.map(convertAggField)),
        keysToBeIncludedInSecAgg: getIncludeKeysField(groupBy).concat(
          groupBy.includes("tags") && !showAllTags ? filteredTagKeys : [],
        ),
        filters: {
          ...filters,
        },
        timezone: moment.tz.guess(),
      });
      const updatedPromise = promise.then((response: GroupStatsResponse) => {
        if (isUndefined(response[businessUnitId!])) {
          return [];
        }
        const stats: GroupStats[] = [
          {
            name: businessUnitId + "",
            displayName: businessUnitId + "",
            riskScore: response[businessUnitId!].riskScore,
            count: response[businessUnitId!].count,
            stats: pick(response[businessUnitId!], groupBy),
          },
        ];
        groupBy.forEach((key) => {
          if (
            isUndefined(stats[0].stats[key]) &&
            !isUndefined(response[businessUnitId!][convertAggField(key)])
          ) {
            stats[0].stats[key] =
              response[businessUnitId!][convertAggField(key)];
          }
        });
        setTotalGroupCount(stats[0].stats[groupBy[0]]);
        return stats;
      });
      return {
        cancel,
        promise: updatedPromise,
      };
    },
    [groupBy, businessUnitId, setTotalGroupCount, showAllTags],
  );

  const renderFinalItems = useCallback<RenderFinalItems>(
    ({
      filters,
      isParentGroupSelected,
      parentFieldName,
      parentFieldValue,
      parentFilterId,
    }) => {
      return (
        <FilteredFindingListTable
          {...{
            extraFilterInfo: filters,
            isParentGroupSelected,
            parentFieldName,
            parentFieldValue,
            parentFilterId,
          }}
        />
      );
    },
    [],
  );

  const countCellRenderer = useCallback<CountCellRenderer>(
    (filters, { fieldName, fieldValue }) => {
      return (
        <FindingGroupBreakdown
          filters={filters}
          fieldName={fieldName}
          fieldValue={fieldValue}
        />
      );
    },
    [],
  );

  const groupByFieldRenderer = useCallback(
    (field: string): RenderMethodType => {
      return GroupByFieldRender({
        field,
        productList,
        subProductList,
        filterValues,
      });
    },
    [productList, subProductList, filterValues],
  );

  const contextValue = useMemo(
    () => ({
      width,
      appliedFilters: actualAppliedFilters,
      fetchStats,
      fetchAllStats,
      columns: allColumns,
      groupByFieldRenderer,
      renderFinalItems,
      convertFilterField,
      getFieldValue,
      name: "Findings",
      GroupDescriptionComponent: GroupByDescription,
      redirectTo: "/findings",
      getCustomColumnTitles,
      disableSelectRootGroup: disableSelectAllFindings,
      countCellRenderer,
      countColumnOverride,
    }),
    [
      width,
      actualAppliedFilters,
      fetchStats,
      allColumns,
      fetchAllStats,
      renderFinalItems,
      countCellRenderer,
      groupByFieldRenderer,
    ],
  );

  return (
    <div ref={(r) => (ref.current = r || undefined)}>
      <FindingBreakdownProvider
        filters={actualAppliedFilters}
        groupBy={groupBy}
      >
        <ACMultiGroupingTableContext.Provider value={contextValue}>
          <ACMultiGroupingTable
            showSecondaryCounts={!excludeAggregationCardinality}
            groupBy={groupBy}
            index={0}
            otherGroupsVisible={otherGroupsVisible}
            setOtherGroupsVisible={setOtherGroupsVisible}
          />
        </ACMultiGroupingTableContext.Provider>
      </FindingBreakdownProvider>
    </div>
  );
}

function FilteredFindingListTable({
  extraFilterInfo,
  parentFieldName,
  parentFieldValue,
  parentFilterId,
  isParentGroupSelected,
}: {
  extraFilterInfo?: FilterInfo;
  parentFieldName: string;
  parentFieldValue: FilterValue;
  parentFilterId: string;
  isParentGroupSelected: boolean;
}) {
  const filterId = `${parentFilterId ? parentFilterId + ":" : ""}-individual`;
  const { addFilter: addFilterToBucket, filterMap } = useFindingBucket();
  const selectedFindingIds = filterMap[filterId]?.id_term;
  const [loading, setLoading] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ListFindingResponse>({});
  const {
    tableCustomizationHelper,
    actualAppliedFilters,
    paginationControls: { groupBy, onGroupByChange, sortInfo, onSortChange },
    showFindingDetail,
    isResolutionNoteColumnVisible,
  } = useFindingListContext();
  const { columns: originalColumns, onColumnOrderChange } =
    tableCustomizationHelper;

  const entityManagerParamsRef = useRef<
    Pick<EntityNavigatorParams, "payload" | "totalEntities">
  >({ payload: {}, totalEntities: 0 });

  const { page, pageSize, onPageChange } = usePaginationControls();

  const { globalFilterHash } = useGlobalFilterContext();

  useEffect(() => {
    // if parent and child both are selected then unselect child
    if (isParentGroupSelected && !isEmpty(selectedFindingIds)) {
      addFilterToBucket(filterId, {}, 0);
    }
  }, [isParentGroupSelected, selectedFindingIds, addFilterToBucket, filterId]);

  const fetchFindings = useCallback(() => {
    setLoading(true);
    const sortColumns: FindingListSortOrder[] = [];
    forEach(sortInfo, (order, key) => {
      sortColumns.push({
        property: key,
        direction: order?.replace("end", ""),
      });
    });

    //
    entityManagerParamsRef.current.payload = {
      page: page - 1,
      size: pageSize,
      sortColumns,
      filters: {
        ...actualAppliedFilters,
        ...extraFilterInfo,
        [convertFilterField(parentFieldName, parentFieldValue)]: getFieldValue(
          parentFieldName,
          parentFieldValue,
        ),
      },
    };
    //

    const { promise, cancel } = FindingAPI.getAll({
      ...entityManagerParamsRef.current.payload,
      ticketStatusRequired: true,
      commentCountRequired: true,
      addLastResolutionNote: isResolutionNoteColumnVisible,
    } as FindingFiltersRequestDto);
    promise
      .then((response: ListFindingResponse) => {
        setResponse(response);
        entityManagerParamsRef.current.totalEntities = response.totalElements!;
      })
      .catch(() =>
        setResponse((prevResponse) => ({
          content: [],
          totalElements: prevResponse.totalElements,
        })),
      )
      .finally(() => {
        setLoading(false);
      });

    return cancel;
  }, [
    page,
    pageSize,
    actualAppliedFilters,
    sortInfo,
    extraFilterInfo,
    parentFieldName,
    parentFieldValue,
    isResolutionNoteColumnVisible,
  ]);

  useEffect(() => {
    const cancel = fetchFindings();
    const refreshFindings = () => fetchFindings();
    window.addEventListener("refreshList", refreshFindings);
    return () => {
      cancel();
      window.removeEventListener("refreshList", refreshFindings);
    };
  }, [fetchFindings, globalFilterHash]);

  const rowSelection = useMemo<TableProps["rowSelection"]>(() => {
    return {
      type: "checkbox",
      columnWidth: 40,
      onChange: (rowKeys) => {
        addFilterToBucket(
          filterId,
          isEmpty(rowKeys) ? {} : { id_term: rowKeys },
          rowKeys.length,
        );
      },
      selectedRowKeys: isParentGroupSelected
        ? map(response.content, ({ id }) => id!)
        : (selectedFindingIds as React.Key[]) || [],
      getCheckboxProps: () => ({ disabled: isParentGroupSelected }),
      preserveSelectedRowKeys: true,
    };
  }, [
    addFilterToBucket,
    filterId,
    selectedFindingIds,
    isParentGroupSelected,
    response.content,
  ]);

  // Overriding id column since we need to listen onclick
  const findingIdColumn = getFindingIdColumn({
    showFindingDetail: (e) => {
      showFindingDetail(e, {
        ...entityManagerParamsRef.current,
      });
    },
  });
  const originalFindingIDColumn = _.find(originalColumns, { dataIndex: "id" });
  const columns = [
    {
      ...findingIdColumn,
      width: originalFindingIDColumn?.width || findingIdColumn.width,
      onHeaderCell: originalFindingIDColumn?.onHeaderCell,
      draggable: originalFindingIDColumn?.draggable,
    },
    ...originalColumns.filter((c) => c.key !== findingIdColumn.key),
  ];

  return (
    <>
      <ACColumnDragWrapper onDragEnd={onColumnOrderChange}>
        <ACTable<FindingSummary>
          columns={columns}
          onSortChange={onSortChange}
          sortInfo={sortInfo}
          loading={loading}
          dataSource={response.content}
          pagination={
            response.totalElements && response.totalElements > 10
              ? { pageSize }
              : false
          }
          rowSelection={rowSelection}
          {...CommonTableProps}
          customizationHelpers={tableCustomizationHelper}
          fullPage
          groupBy={groupBy}
          onGroupByChange={onGroupByChange}
        />
      </ACColumnDragWrapper>
      {response.totalElements && response.totalElements > 10 ? (
        <ACPaginationBar
          onChange={onPageChange}
          page={page}
          pageSize={pageSize}
          size="md"
          total={response.totalElements || 0}
          showSizeChanger={false}
          showEdges={false}
          bordered={false}
        />
      ) : null}
    </>
  );
}

function FindingGroupBreakdown({
  fieldName,
  fieldValue,
  filters,
}: {
  fieldName: string;
  fieldValue: FilterValue;
  filters: FilterInfo;
}) {
  const { fetchData, useData } = useFindingBreakdownProvider();
  const { businessUnitId } = useActiveUserContext();
  const finalFilters = useMemo(
    () => ({
      ...filters,
      [convertFilterField(fieldName, fieldValue)]: getFieldValue(
        fieldName,
        fieldValue,
      ),
    }),
    [filters, fieldName, fieldValue],
  );

  const dataObject = useData(finalFilters);
  const data = dataObject?.data;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData(finalFilters);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [fetchData, finalFilters, data]);

  if (!data) {
    return <LoadingIcon />;
  }
  const stat = dataObject.data?.stats?.[businessUnitId!]?.severity;
  return stat ? (
    <ACFindingBreakdownWrapper
      findingSeverity={stat}
      activeSeverityKeys={ALL_SEVERITIES}
      payload={{ filters: finalFilters }}
    />
  ) : (
    <>-</>
  );
}

const GroupByFieldRender = ({
  field,
  productList,
  subProductList,
  filterValues,
}: {
  field: string;
  productList: NameIdPair[];
  subProductList: NameIdPair[];
  filterValues: FilterOptionInfo;
}): RenderMethodType => {
  return (record) => {
    if (field.startsWith("keyValue.")) {
      return CellRenderers.VALUE_OR_NA(extractTagValue(record.name));
    }
    switch (field) {
      case "severity":
        return <ACSeverityTag value={record.name as SeverityType} />;
      case "slaDueDate":
      case "triageDueDate":
      case "remediationDueDate":
      case "craEarlyWarningDue":
      case "craNotificationDue":
      case "craFinalReportDue":
      case "craUserDisclosureDue":
        return CellRenderers.DATE(+record.name);
      case "resolutionSlaStatus":
      case "triageSlaStatus":
      case "remediationSlaStatus":
        return RENDER_FINDING_SLA_STATUS_FILTERS(record.name);
      case ARMORCODE_GROUP_KEY:
        return record.name.replace(`${ARMORCODE_GROUP_KEY}:`, "");
      case "correlationName":
        return correlationNameRenderer(record.name || record.displayName);
      case "riskRating":
        return <RiskRatingTag rating={record.name as RiskRating} />;
      default:
        return GroupByFieldName(
          field,
          filterValues,
          record,
          productList,
          subProductList,
        );
    }
  };
};

export const correlationNameRenderer = (name: string) => {
  if (
    name &&
    [
      "ContainerImage:",
      "SubProductName:",
      "ContainerFilePath:",
      "Asset:",
      "HostName:",
    ].some((subString) => name.includes(subString))
  ) {
    const stringToDisplay = name.split(",").shift();
    return stringToDisplay && name.includes("SubProductName")
      ? `${getVocabulary("subProduct")}:${stringToDisplay.split(":").pop()}`
      : stringToDisplay;
  }
  return name;
};

const GroupByFieldName = (
  field: string,
  filterValues: FilterOptionInfo,
  record: GroupStats,
  productList: NameIdPair[],
  subProductList: NameIdPair[],
): string => {
  return (() => {
    switch (field) {
      case "status":
        return FINDING_STATUS_VOCABULARY_MAP[record.name!]
          ? getVocabulary(FINDING_STATUS_VOCABULARY_MAP[record.name!])
          : record.name;
      case "exploited":
      case "isCISAKEV":
        return renderBooleanValues(record.name);
      case "product": {
        const product = find(productList, {
          id: +record.name!,
        }) as FilterOptionNameId;
        return product?.name || record.name!;
      }
      case "subProduct": {
        const subProduct = find(subProductList, {
          id: +record.name!,
        }) as FilterOptionNameId;
        return subProduct?.name || record.name!;
      }
      case "owner":
      case "team":
      case "armorcodeProjects":
      case "teamId": {
        const option = find(filterValues?.[field], {
          id: +record.name!,
        }) as FilterOptionNameId;
        return option?.name || record.name!;
      }
      case "owaspTop10_2021":
      case "cweTop25_2024": {
        const cwe = find(filterValues?.[field], {
          id: record.name!,
        }) as FilterOptionNameId;
        return cwe?.name || record.name!;
      }
      default:
        return record.name!;
    }
  })();
};

const getFieldValue = (
  fieldName: string,
  fieldValue: FilterValue,
): FilterValue[] => {
  if (fieldValue === "null" || fieldValue === null) {
    return [null];
  }
  if (fieldName.startsWith("keyValue.") && isString(fieldValue)) {
    return [
      // exact match filter
      `"${extractTagValue(fieldValue)}"`,
    ];
  }
  if (checkIfDateColumns(fieldName)) {
    return [
      moment(+fieldValue).startOf("day").utc().format(),
      moment(+fieldValue).endOf("day").utc().format(),
    ];
  }

  if (KEYS_WITH_IDS.includes(fieldName) && isNumber(+fieldName)) {
    return [+fieldValue];
  }

  if (
    ["tags", "keyValue", ARMORCODE_GROUP_KEY].includes(fieldName) &&
    isString(fieldValue)
  ) {
    const [, ...valueParts] = fieldValue.split(":");
    return compact([
      valueParts.length ? `"${valueParts.join(":")}"` : undefined,
    ]);
  }

  // ENG-49971 - fields supporting partial search return data from other group due to prefix match
  // hence applying complete search format with double quotes
  return [
    PartialSearchSupportedFields.includes(fieldName)
      ? `"${fieldValue}"`
      : fieldValue,
  ];
};

export const FindingListGroupByTableWrapperContext = React.createContext<
  number | undefined
>(0);

const convertFilterField = (key: string, value?: FilterValue) => {
  // ENG-55882 converting hierarchy and tags to keyValue as these filters are applied on UI and should show in filter bar
  if (key === ARMORCODE_GROUP_KEY) {
    return `keyValue.${ARMORCODE_GROUP_KEY}`;
  }
  if (key === "tags") {
    if (isString(value)) {
      // ENG-62109 special handling for tags - fields having multiple values are inconsistent as filter gets overridden when its group is expanded
      return `keyValue.${value.split(":")[0]}`;
    }
    return "keyValue";
  }

  return key;
};

const countColumnOverride = {
  title: "Findings",
};
